import React from 'react';
import Icon from '../../../components/AppIcon';

const PlatformHealthMetrics = ({ timeRange }) => {
  const healthMetrics = [
    {
      category: 'API Performance',
      icon: 'Zap',
      color: 'bg-primary',
      status: 'healthy',
      metrics: [
        { label: 'Requests/min', value: '1,247', status: 'good' },
        { label: 'Avg Response', value: '145ms', status: 'good' },
        { label: 'Error Rate', value: '0.24%', status: 'good' },
        { label: 'P99 Latency', value: '342ms', status: 'warning' }
      ]
    },
    {
      category: 'Transfer Engine',
      icon: 'ArrowRightLeft',
      color: 'bg-success',
      status: 'healthy',
      metrics: [
        { label: 'Processing Time', value: '2.3s', status: 'good' },
        { label: 'Success Rate', value: '99.7%', status: 'good' },
        { label: 'Queue Depth', value: '12', status: 'good' },
        { label: 'Downtime', value: '0min', status: 'good' }
      ]
    },
    {
      category: 'Database',
      icon: 'Database',
      color: 'bg-accent',
      status: 'attention',
      metrics: [
        { label: 'Query Time', value: '23ms', status: 'good' },
        { label: 'Connections', value: '78/100', status: 'warning' },
        { label: 'Cache Hit', value: '94.2%', status: 'good' },
        { label: 'Disk Usage', value: '67%', status: 'good' }
      ]
    },
    {
      category: 'Configuration',
      icon: 'Settings',
      color: 'bg-warning',
      status: 'healthy',
      metrics: [
        { label: 'Drift Detected', value: '0', status: 'good' },
        { label: 'Unauthorized', value: '0', status: 'good' },
        { label: 'Last Change', value: '2h ago', status: 'good' },
        { label: 'Backup Status', value: 'Current', status: 'good' }
      ]
    }
  ];

  const complianceActions = [
    {
      action: 'Rule Change: High-Risk Country List Updated',
      user: 'Sarah Johnson',
      timestamp: '2h ago',
      category: 'compliance_rule'
    },
    {
      action: 'Agent Tier Upgrade: AGT-045 → Tier 2',
      user: 'Michael Chen',
      timestamp: '5h ago',
      category: 'agent_tier'
    },
    {
      action: 'Admin Privilege Granted: emma.d@example.com',
      user: 'Sarah Johnson',
      timestamp: 'Yesterday',
      category: 'admin_privilege'
    },
    {
      action: 'AML Threshold Modified: $5,000 → $7,500',
      user: 'Compliance Team',
      timestamp: '2 days ago',
      category: 'compliance_rule'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-success';
      case 'warning': return 'text-warning';
      case 'attention': return 'text-accent';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getMetricStatusColor = (status) => {
    switch (status) {
      case 'good': return 'text-success';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'compliance_rule': return 'Shield';
      case 'agent_tier': return 'UserCog';
      case 'admin_privilege': return 'Key';
      default: return 'FileText';
    }
  };

  return (
    <div className="space-y-6">
      {/* Health Metrics */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Activity" size={20} />
            <span>System Health</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time platform monitoring
          </p>
        </div>
        <div className="p-4 space-y-4">
          {healthMetrics?.map((metric, index) => (
            <div key={index} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg ${metric?.color} flex items-center justify-center`}>
                    <Icon name={metric?.icon} size={20} color="white" />
                  </div>
                  <h4 className="font-medium text-foreground">{metric?.category}</h4>
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
                  ${metric?.status === 'healthy' ? 'bg-success/10 text-success' :
                    metric?.status === 'warning' ? 'bg-warning/10 text-warning' :
                    metric?.status === 'attention'? 'bg-accent/10 text-accent' : 'bg-error/10 text-error'
                  }`}>
                  {metric?.status?.toUpperCase()}
                </span>
              </div>
              <div className="space-y-2">
                {metric?.metrics?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{item?.label}</span>
                    <span className={`font-medium ${getMetricStatusColor(item?.status)}`}>
                      {item?.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Actions Log */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="FileText" size={20} />
            <span>Key Compliance Actions</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Recent regulatory changes
          </p>
        </div>
        <div className="p-4 space-y-3">
          {complianceActions?.map((action, index) => (
            <div key={index} className="border-l-2 border-primary/50 pl-4 py-2">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={getCategoryIcon(action?.category)} size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground mb-1">
                    {action?.action}
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="User" size={12} />
                      <span>{action?.user}</span>
                    </span>
                    <span>•</span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span>{action?.timestamp}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlatformHealthMetrics;